<template>
  <v-card class="card-center pb-2" outlined>
    <v-container class="px-4 py-4" fluid>
      <v-card flat>
        <v-card-title class="py-0">
          {{ $t('ELE0028') }}
        </v-card-title>
        <v-card-text class="px-0 py-0">
          <validation-observer v-slot="{ invalid }">
            <v-row>
              <v-col cols="3">
                <PartGroupInput v-model="searchCondition.partGroupCd" />
              </v-col>
              <v-col cols="3">
                <MmmmApplCatInput v-model="searchCondition.applCatCd" />
              </v-col>
              <v-col cols="3">
                <SectionInput v-model="searchCondition.sectionCd" />
              </v-col>
              <v-col cols="3">
                <TextFieldInput :label="$t('ELE0052')" v-model="searchCondition.mmmmNo" />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="3">
                <SelectMultipleInput
                  :items="mmmmApprovalStatusCdItems"
                  itemText="name"
                  itemValue="value"
                  :label="$t('ELE0218')"
                  v-model="searchCondition.statusCds"
                />
              </v-col>
              <v-col cols="3">
                <SelectMultipleInput
                  :items="mmmmDispositionCdItems"
                  itemText="name"
                  itemValue="value"
                  :label="$t('ELE0505')"
                  v-model="searchCondition.dispositionCds"
                />
              </v-col>
              <v-col cols="3">
                <ManufacturerInput v-model="searchCondition.manufacturerCd" />
              </v-col>
              <v-col>
                <TextFieldInput :label="$t('ELE0031')" v-model="searchCondition.partCd" />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="3">
                <TextFieldInput :label="$t('ELE0224')" v-model="searchCondition.model" />
              </v-col>
              <v-col cols="3">
                <TextFieldInput :label="$t('ELE0049')" v-model="searchCondition.lotNo" />
              </v-col>
              <v-col cols="2" class="pb-0">
                <TextFieldInputWithDatePicker :label="$t('ELE0223')" v-model="searchCondition.issueDateFrom" required />
              </v-col>
              <span class="px-4 pt-5 pb-0"> {{ $t('ELE0141') }} </span>
              <v-col cols="2" class="pb-0">
                <TextFieldInputWithDatePicker :label="$t('ELE0223')" v-model="searchCondition.issueDateTo" required />
              </v-col>
            </v-row>
            <v-row class="pb-0 mb-0">
              <v-col cols="3">
                <MemberInput :label="$t('ELE0235')" v-model="searchCondition.requesterCd" />
              </v-col>
              <v-col cols="3">
                <MemberInput :label="$t('ELE0279')" v-model="searchCondition.nextCheckerCd" />
              </v-col>
              <v-col>
                <Checkbox :label="$t('ELE0518')" class="mr-4" v-model="searchCondition.noInputCmsOnly" />
              </v-col>
              <v-col class="d-flex flex-row justify-end">
                <Btn
                  class="mr-4"
                  :label="$t('ELE0040')"
                  icon="mdi-download"
                  @click="onClickDownload"
                  :disabled="invalid"
                />
                <BtnSearch @click="onClickSearch" :disabled="invalid" />
              </v-col>
            </v-row>
          </validation-observer>
        </v-card-text>
      </v-card>
      <v-card outlined>
        <v-card-title class="py-0"> </v-card-title>
        <v-card outlined>
          <v-data-table :headers="headers" :items="items" fixed-header dense>
            <template v-slot:header>
              <thead>
                <tr>
                  <th colspan="24" class="v-data-table__divider"></th>
                  <th colspan="3" class="v-data-table__divider">{{ $t('ELE0233') }}</th>
                  <th colspan="3" class="v-data-table__divider">{{ $t('ELE0278') }}</th>
                </tr>
              </thead>
            </template>
            <template v-slot:item.action="{ item }">
              <IconInquiry @click="onClickInquiry(item.mmmmNo)" />
            </template>
            <template v-slot:item.issueDateMMYY="{ item }">
              <td class="v-data-table__divider">{{ formatDate(item.issueDate, 'MMM YYYY') }}</td>
            </template>
            <template v-slot:item.lastUpdatedDatetime="{ item }">
              <td class="v-data-table__divider">{{ toLocalDate(item.lastUpdatedDatetime) }}</td>
            </template>
            <template v-slot:item.dispositionCd="{ item }">
              <td class="v-data-table__divider">{{ getEnumlang('MmmmDispositionCd.', item.dispositionCd) }}</td>
            </template>
            <template v-slot:item.statusCd="{ item }">
              <td class="v-data-table__divider">{{ $t(`MmmmApprovalStatusCd.${item.statusCd}`) }}</td>
            </template>
          </v-data-table>
        </v-card>
      </v-card>
    </v-container>

    <v-dialog v-if="mmmmDialog" v-model="mmmmDialog" scrollable persistent>
      <v-card>
        <v-card-title class="primary white--text py-2">
          {{ $t('ELE0491') }}
          <v-spacer />
          <Btn icon="mdi-close" @click="mmmmDialog = false" depressed iconProp color="white" />
        </v-card-title>
        <v-card-text class="px-0">
          <MmmmRequestForm :propsMmmmNo="selectedMmmmNo" @close=";(mmmmDialog = false), onClickSearch()" isModal />
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { API } from '@/plugins/api'
import APIUtils from '@/mixin/api-utils.js'
import Message from '@/mixin/message-utils.js'
import Loading from '@/mixin/loading-utils.js'
import StringUtils from '@/mixin/string-utils.js'
import DateUtils from '@/mixin/date-utils.js'
import { ValidationObserver } from 'vee-validate'
import { MmmmDispositionCd, MmmmApprovalStatusCd } from '@/enum.js'
import dayjs from 'dayjs'
import { Storage } from '@/plugins/storage.js'

import Btn from '@/components/atoms/base/button/Btn'
import TextFieldInput from '@/components/atoms/base/text-field/TextFieldInput'
import TextFieldInputWithDatePicker from '@/components/atoms/base/text-field/TextFieldInputWithDatePicker'
import SelectSingleInput from '@/components/atoms/base/select/SelectSingleInput'
import SelectMultipleInput from '@/components/atoms/base/select/SelectMultipleInput'
import Checkbox from '@/components/atoms/base/checkbox/Checkbox'
import BtnSearch from '@/components/atoms/button/BtnSearch'
import IconInquiry from '@/components/atoms/icon/IconInquiry'
import CompanyInput from '@/components/atoms/select/CompanyInput'
import PreparedCompanyInput from '@/components/atoms/select/PreparedCompanyInput'

import PartGroupInput from '@/components/atoms/select/PartGroupInput'
import MmmmApplCatInput from '@/components/atoms/select/MmmmApplCatInput'
import SectionInput from '@/components/atoms/select/SectionInput'
import ManufacturerInput from '@/components/atoms/select/ManufacturerInput'
import MemberInput from '@/components/atoms/select/MemberInput'

import MmmmRequestForm from '@/components/organisms/terminal/form/MmmmRequestForm'

export default {
  name: 'SearchMmmmHistoryForm',

  mixins: [APIUtils, Message, Loading, StringUtils, DateUtils],

  components: {
    ValidationObserver,
    Btn,
    TextFieldInput,
    TextFieldInputWithDatePicker,
    SelectSingleInput,
    SelectMultipleInput,
    Checkbox,
    BtnSearch,
    IconInquiry,
    CompanyInput,
    PreparedCompanyInput,
    PartGroupInput,
    MmmmApplCatInput,
    SectionInput,
    ManufacturerInput,
    MemberInput,
    MmmmRequestForm,
  },

  props: {
    propsMmmmNo: {
      type: String,
      required: false,
      default: '',
    },
    propsIsWaiting: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      mmmmDialog: false,
      selectedMmmmNo: '',
      items: [],
      headers: [
        { text: '', value: 'action', divider: true, sortable: false },
        { text: this.$t('ELE0052'), value: 'mmmmNo', divider: true },
        { text: this.$t('ELE0223'), value: 'issueDate', divider: true },
        { text: this.$t('ELE0442'), value: 'issueDateMMYY', divider: true },
        { text: this.$t('ELE0536'), value: 'lastUpdatedDatetime', divider: true },
        { text: this.$t('ELE0438'), value: 'applCatName', divider: true },
        { text: this.$t('ELE0443'), value: 'severityName', divider: true },
        { text: this.$t('ELE0446'), value: 'reasonName', divider: true },
        { text: this.$t('ELE0447'), value: 'reasonDtlName', divider: true },
        { text: this.$t('ELE0031'), value: 'partCd', divider: true },
        { text: this.$t('ELE0032'), value: 'partName', divider: true },
        { text: this.$t('ELE0051'), value: 'moldNo', divider: true },
        { text: this.$t('ELE0444'), value: 'numOfCav', divider: true },
        { text: this.$t('ELE0445'), value: 'cavNo', divider: true },
        { text: this.$t('ELE0224'), value: 'model', divider: true },
        { text: this.$t('ELE0394'), value: 'manufacturerCd', divider: true },
        { text: this.$t('ELE0057'), value: 'manufacturerName', divider: true },
        { text: this.$t('ELE0220'), value: 'currentRouteName', divider: true },
        { text: this.$t('ELE0287'), value: 'partCatName', divider: true },
        { text: this.$t('ELE0049'), value: 'lotNo', divider: true },
        { text: this.$t('ELE0137'), value: 'oqcInspIndexNo', divider: true },
        { text: this.$t('ELE0440'), value: 'dispositionCd', divider: true },
        { text: this.$t('ELE0218'), value: 'statusCd', divider: true },
        { text: this.$t('ELE0279'), value: 'currentCheckedBy', divider: true },
        { text: this.$t('ELE0235'), value: 'firstPreparedBy', divider: true },
        { text: this.$t('ELE0236'), value: 'firstCheckedBy', divider: true },
        { text: this.$t('ELE0237'), value: 'firstApprovedBy', divider: true },
        { text: this.$t('ELE0277'), value: 'finalRequestBy', divider: true },
        { text: this.$t('ELE0236'), value: 'finalCheckedBy', divider: true },
        { text: this.$t('ELE0237'), value: 'finalApprovedBy' },
      ],
      searchCondition: {
        partGroupCd: '',
        applCatCd: '',
        sectionCd: '',
        mmmmNo: '',
        dispositionCds: [],
        statusCds: [],
        manufacturerCd: '',
        partCd: '',
        model: '',
        lotNo: '',
        issueDateFrom: dayjs().subtract(30, 'd').format('YYYY-MM-DD'), // Notification.vueで処理待ちのWF検索するところにも同じ初期値あり
        issueDateTo: dayjs().format('YYYY-MM-DD'),
        requesterCd: '',
        nextCheckerCd: '',
        noInputCmsOnly: false,
      },
    }
  },

  computed: {
    mmmmDispositionCdItems: {
      get() {
        // プルダウン選択肢をenumから生成
        return Object.values(MmmmDispositionCd).map((item) => {
          return { name: item + ' : ' + this.$t(`MmmmDispositionCd.${item}`), value: item }
        })
      },
    },
    mmmmApprovalStatusCdItems: {
      get() {
        // プルダウン選択肢をenumから生成
        return Object.values(MmmmApprovalStatusCd).map((item) => {
          return { name: this.$t(`MmmmApprovalStatusCd.${item}`), value: item }
        })
      },
    },
  },

  methods: {
    getEnumlang(key, value) {
      // langのenumからメッセージ取得
      let lang = ''
      if (value) {
        lang = this.$t(key + value)
      }
      return lang
    },
    formatDate(date, format) {
      let ret = ''
      if (date) {
        ret = dayjs(date).format(format)
      }
      return ret
    },
    onClickInquiry(mmmmNo) {
      this.selectedMmmmNo = mmmmNo
      this.mmmmDialog = true
    },
    async onClickSearch() {
      try {
        this.startLoading()

        const params = {
          userCompanyCd: this.$store.getters.companyCd,
          partGroupCd: this.searchCondition.partGroupCd,
          applCatCd: this.searchCondition.applCatCd,
          sectionCd: this.searchCondition.sectionCd,
          mmmmNo: this.searchCondition.mmmmNo,
          dispositionCds: this.searchCondition.dispositionCds,
          statusCds: this.searchCondition.statusCds,
          manufacturerCd: this.searchCondition.manufacturerCd,
          partCd: this.searchCondition.partCd,
          model: this.searchCondition.model,
          lotNo: this.searchCondition.lotNo,
          issueDateFrom: this.searchCondition.issueDateFrom,
          issueDateTo: this.searchCondition.issueDateTo,
          requesterCd: this.searchCondition.requesterCd,
          nextCheckerCd: this.searchCondition.nextCheckerCd,
          noInputCmsOnly: this.searchCondition.noInputCmsOnly,
        }

        this.items = await API.get('/mmmm', params)
      } catch (error) {
        this.items = []
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0491') })))
      } finally {
        this.stopLoading()
      }
    },
    async onClickDownload() {
      try {
        this.startLoading()

        const params = {
          userCompanyCd: this.$store.getters.companyCd,
          partGroupCd: this.searchCondition.partGroupCd,
          applCatCd: this.searchCondition.applCatCd,
          sectionCd: this.searchCondition.sectionCd,
          mmmmNo: this.searchCondition.mmmmNo,
          dispositionCds: this.searchCondition.dispositionCds,
          statusCds: this.searchCondition.statusCds,
          manufacturerCd: this.searchCondition.manufacturerCd,
          partCd: this.searchCondition.partCd,
          model: this.searchCondition.model,
          lotNo: this.searchCondition.lotNo,
          issueDateFrom: this.searchCondition.issueDateFrom,
          issueDateTo: this.searchCondition.issueDateTo,
          requesterCd: this.searchCondition.requesterCd,
          nextCheckerCd: this.searchCondition.nextCheckerCd,
          noInputCmsOnly: this.searchCondition.noInputCmsOnly,
        }

        const filePath = await API.get('/mmmm/file', params)
        Storage.downloadFile(filePath)
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0491') })))
      } finally {
        this.stopLoading()
      }
    },
  },

  mounted() {
    if (this.propsMmmmNo) {
      this.searchCondition.mmmmNo = this.propsMmmmNo
      this.onClickSearch()
    } else if (this.propsIsWaiting) {
      this.searchCondition.nextCheckerCd = this.$store.getters.userCd
      this.onClickSearch()
    }
  },
}
</script>
